import { useCollection } from "@amzn/awsui-collection-hooks";
import {
  Badge,
  Box,
  Button,
  CollectionPreferences,
  Header,
  Pagination,
  SegmentedControl,
  SpaceBetween,
  Table,
  TableProps,
  TextFilter,
  Toggle,
} from "@amzn/awsui-components-console";
import React, { useMemo, useState } from "react";

import { TablePreferences } from "@/components/chat/types";
import useAdminMetadataFields from "@/hooks/admin/useAdminMetadataFields";
import useModal from "@/hooks/useModal";
import {
  ADMIN_DEFAULT_PAGE_SIZE,
  adminPageSizePreference,
} from "@/utils/adminTable";
import { formatTimestamp } from "@/utils/utils";

import hoverStyles from "../common/hoverRows.module.css";
import EmptyState from "../EmptyState";
import { MetadataFieldConfig, MetadataFieldType } from "./types";

/**
 * Badge colour per field type, grouped by how the field is filled in rather
 * than one colour per type: blue for a fixed vocabulary the agent can enumerate,
 * green for temporal values, grey for open input.
 *
 * Only the three colours already in use here — a `Record` over the full type
 * union, so adding a type to `MetadataFieldType` without adding it here is a
 * compile error rather than a blank badge.
 */
const TYPE_BADGE_COLOR: Record<MetadataFieldType, "blue" | "grey" | "green"> = {
  dropdown: "blue",
  boolean: "blue",
  date: "green",
  datetime: "green",
  string: "grey",
  text: "grey",
  tag_cloud: "grey",
  integer: "grey",
  float: "grey",
};

/**
 * Admin-facing name per field type.
 *
 * "Boolean" rather than Iconik's own "Yes/No" wording: Yes/No reads like a
 * two-option dropdown, which is a different thing on this page.
 */
const TYPE_LABEL: Record<MetadataFieldType, string> = {
  dropdown: "Dropdown",
  boolean: "Boolean",
  string: "String",
  text: "Text",
  tag_cloud: "Tags",
  integer: "Integer",
  float: "Decimal",
  date: "Date",
  datetime: "Date/Time",
};

const MAX_VALUE_CHIPS = 5;

/**
 * Width of the Field name column, in pixels. This single value drives the
 * column's preferred width, its minimum width, and a matching floor on the
 * cell content — all three are needed to actually hold the column open. Adjust
 * this one constant to resize the column; see the column definition for why a
 * preferred width on its own has no effect.
 */
const FIELD_NAME_COLUMN_WIDTH = 520;

/** Render who last changed a field's governance flags, and when. */
const LastChangedByCell: React.FC<{ field: MetadataFieldConfig }> = ({ field }) => {
  if (!field.lastChangedBy) {
    return (
      <div data-testid={`last-changed-by-${field.fieldName}`}>
        <Box color="text-status-inactive">—</Box>
      </div>
    );
  }
  return (
    <div data-testid={`last-changed-by-${field.fieldName}`}>
      <SpaceBetween size="xxs">
        <Box>{field.lastChangedBy}</Box>
        {field.lastChangedAt && (
          <Box color="text-status-inactive" fontSize="body-s">
            {formatTimestamp(field.lastChangedAt)}
          </Box>
        )}
      </SpaceBetween>
    </div>
  );
};

/** Render every value of a dropdown field as a wrapping list of badges. */
const AllValueBadges: React.FC<{ options: string[] }> = ({ options }) => (
  <SpaceBetween direction="horizontal" size="xxs">
    {options.map((opt) => (
      <Badge key={opt} color="grey">
        {opt}
      </Badge>
    ))}
  </SpaceBetween>
);

/**
 * Render a dropdown field's options as the first few badges plus a "+N more"
 * control. The "+N more" is a button: hovering it reveals every value via the
 * native tooltip, and clicking it opens a modal listing all values in full.
 */
const ValuesCell: React.FC<{ field: MetadataFieldConfig }> = ({ field }) => {
  const options = field.options ?? [];
  // Hook is called unconditionally (before the early return) to respect the
  // rules of hooks; the modal is only rendered when there are extra values.
  const { toggle, ModalComponent } = useModal({
    title: `${field.displayLabel} — all values`,
    content: <AllValueBadges options={options} />,
  });

  if (field.type !== "dropdown" || options.length === 0) {
    return (
      <Box color="text-status-inactive" fontSize="body-s">
        {field.valueHint ?? "—"}
      </Box>
    );
  }

  const shown = options.slice(0, MAX_VALUE_CHIPS);
  const remaining = options.length - shown.length;

  return (
    <>
      <SpaceBetween direction="horizontal" size="xxs">
        {shown.map((opt) => (
          <Badge key={opt} color="grey">
            {opt}
          </Badge>
        ))}
        {remaining > 0 && (
          <button
            type="button"
            // Hover reveals every value; click opens the full modal below.
            title={options.join(", ")}
            onClick={toggle}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              font: "inherit",
              color: "inherit",
              textDecoration: "underline",
            }}
          >
            +{remaining} more
          </button>
        )}
      </SpaceBetween>
      {remaining > 0 && <ModalComponent />}
    </>
  );
};

const MetadataConfiguration: React.FC = () => {
  const { fields, loading, loadFields, setShowInPreview, setAvailableToAgent } =
    useAdminMetadataFields();

  const agentCount = useMemo(
    () => fields.filter((f) => f.availableToAgent).length,
    [fields]
  );
  const previewCount = useMemo(
    () => fields.filter((f) => f.showInPreview).length,
    [fields]
  );

  // Quick governance filter: show all fields, or only those exposed to the
  // agent / shown in the preview panel. Applied before the text filter so the
  // two compose (search within the selected view).
  const [view, setView] = useState<"all" | "agent" | "preview">("all");
  const visibleFields = useMemo(
    () =>
      fields.filter((f) =>
        view === "agent"
          ? f.availableToAgent
          : view === "preview"
            ? f.showInPreview
            : true
      ),
    [fields, view]
  );

  // Rows per page, adjustable via the table's Preferences. Page size is the
  // only preference this table has: the gear offers nothing else, and the table
  // reads nothing else, so seeding any other flag here would be config that can
  // neither be changed nor observed.
  const [preferences, setPreferences] = useState<TablePreferences>({
    pageSize: ADMIN_DEFAULT_PAGE_SIZE,
  });

  const { items, actions, filteredItemsCount, collectionProps, filterProps, paginationProps } =
    useCollection(visibleFields, {
      filtering: {
        empty: <EmptyState title="No metadata fields available" />,
        noMatch: (
          <EmptyState
            title="No matches"
            action={
              <Button onClick={() => actions.setFiltering("")}>
                Clear filter
              </Button>
            }
          />
        ),
        // Match against the field name and the display label.
        filteringFunction: (field: MetadataFieldConfig, text: string) => {
          const q = text.toLowerCase();
          return (
            field.fieldName.toLowerCase().includes(q) ||
            field.displayLabel.toLowerCase().includes(q)
          );
        },
      },
      pagination: { pageSize: preferences.pageSize },
      // Default ordering: alphabetical by the label the table shows, so a field
      // is found where its name says it is. Governance state (shown in preview
      // / available to agent) no longer influences row order, which also means
      // toggling a field never moves its row out from under the click.
      sorting: {
        defaultState: { sortingColumn: { sortingField: "displayLabel" } },
      },
    });

  const columnDefinitions = useMemo<
    TableProps<MetadataFieldConfig>["columnDefinitions"]
  >(
    () => [
      {
        id: "displayLabel",
        header: "Display label",
        sortingField: "displayLabel",
        cell: (field) => field.displayLabel,
      },
      {
        id: "fieldName",
        header: "Field name",
        sortingField: "fieldName",
        // This table is not resizable, so the table applies `width`/`minWidth`
        // straight to the header cell. On their own they are not enough: the
        // table uses auto layout, and because `wrapLines` lets this column's
        // content wrap, the browser is free to ignore the preferred `width` and
        // squeeze the column down to its minimum content width to give room to
        // the other columns. Setting `minWidth` as well as a matching floor on
        // the cell content raises that minimum content width, which is what
        // actually stops the column from collapsing.
        width: FIELD_NAME_COLUMN_WIDTH,
        minWidth: FIELD_NAME_COLUMN_WIDTH,
        cell: (field) => (
          <div style={{ minWidth: FIELD_NAME_COLUMN_WIDTH }}>
            <SpaceBetween size="xxs">
              <Box variant="strong">{field.fieldName}</Box>
              {field.description && (
                <Box color="text-status-inactive" fontSize="body-s">
                  {field.description}
                </Box>
              )}
            </SpaceBetween>
          </div>
        ),
      },
      {
        id: "type",
        header: "Type",
        cell: (field) => (
          <Badge color={TYPE_BADGE_COLOR[field.type]}>
            {TYPE_LABEL[field.type]}
          </Badge>
        ),
      },
      {
        id: "availableToAgent",
        header: "Available to agent",
        cell: (field) => (
          <Toggle
            checked={field.availableToAgent}
            onChange={({ detail }) =>
              setAvailableToAgent(field.fieldName, detail.checked)
            }
            ariaLabel={`Available to agent: ${field.displayLabel}`}
          />
        ),
      },
      {
        id: "showInPreview",
        header: "Show in preview",
        cell: (field) => (
          <Toggle
            checked={field.showInPreview}
            onChange={({ detail }) =>
              setShowInPreview(field.fieldName, detail.checked)
            }
            ariaLabel={`Show in preview: ${field.displayLabel}`}
          />
        ),
      },
      {
        id: "values",
        header: "Values",
        cell: (field) => <ValuesCell field={field} />,
      },
      {
        id: "lastChangedBy",
        header: "Last changed by",
        cell: (field) => <LastChangedByCell field={field} />,
      },
    ],
    [setShowInPreview]
  );

  return (
    <div className={hoverStyles.hoverRows}>
      <Table
        {...collectionProps}
        data-testid="metadata-config-table"
        variant="container"
        // Wrap long cell content (notably the Field name + its description)
        // instead of forcing columns wide enough to fit a single line.
        wrapLines
        columnDefinitions={columnDefinitions}
        items={items}
        loading={loading}
        loadingText="Loading metadata fields"
        trackBy="fieldName"
        empty={<EmptyState title="No metadata fields available" />}
        header={
          <Header
            counter={`(${fields.length})`}
            description={`${agentCount} available to agent · ${previewCount} shown in preview`}
            actions={
              <Button iconName="refresh" onClick={() => loadFields()}>
                Refresh
              </Button>
            }
          >
            Fields
          </Header>
        }
        filter={
          <SpaceBetween size="xs">
            <SegmentedControl
              selectedId={view}
              onChange={({ detail }) =>
                setView(detail.selectedId as "all" | "agent" | "preview")
              }
              label="Filter fields by governance"
              options={[
                { id: "all", text: "All" },
                { id: "agent", text: "Available to agent" },
                { id: "preview", text: "Shown in preview" },
              ]}
            />
            <TextFilter
              {...filterProps}
              filteringPlaceholder="Search fields by name or label"
              countText={
                filteredItemsCount !== undefined
                  ? `${filteredItemsCount} matches`
                  : ""
              }
            />
          </SpaceBetween>
        }
        pagination={<Pagination {...paginationProps} />}
        preferences={
          <CollectionPreferences
            title="Preferences"
            confirmLabel="Confirm"
            cancelLabel="Cancel"
            preferences={preferences}
            pageSizePreference={adminPageSizePreference("fields")}
            onConfirm={({ detail }) =>
              // The fallback keeps the page size defined even if the control
              // reports none, which would leave the table unpaginated.
              setPreferences({ pageSize: detail.pageSize ?? ADMIN_DEFAULT_PAGE_SIZE })
            }
          />
        }
      />
    </div>
  );
};

export default MetadataConfiguration;
